import { paths } from './../types';
import {
  UseMutationOptions,
  UseMutationResult,
  useMutation
} from '@tanstack/react-query';
import { FetchOptions, FetchResponse } from 'openapi-fetch';
import type {
  FilterKeys,
  PathsWithMethod,
  HasRequiredKeys,
  HttpMethod,
  OperationRequestBodyContent
} from 'openapi-typescript-helpers';
import getClient from './../client';

type MutationBody<T> = OperationRequestBodyContent<T> extends never
  ? void
  : undefined extends OperationRequestBodyContent<T>
    ? OperationRequestBodyContent<T> | void
    : OperationRequestBodyContent<T>;

type MutationOptions<T> = {
  mutationOpts?: Partial<
    UseMutationOptions<
      FetchResponse<T>['data'],
      FetchResponse<T>['error'],
      MutationBody<T>
    >
  >;
};

// eslint-disable-next-line @typescript-eslint/ban-types
type CreateUseTypedMutation<Paths extends {}> = {
  useTypedMutation<T extends HttpMethod, P extends PathsWithMethod<Paths, T>>(
    _method: T,
    _url: P,
    ..._options: HasRequiredKeys<
      FetchOptions<FilterKeys<Paths[P], T>>
    > extends never
      ? [
          (
            | (Omit<FetchOptions<FilterKeys<Paths[P], T>>, 'body'> &
                MutationOptions<
                  T extends keyof Paths[P] ? Paths[P][T] : unknown
                >)
            | undefined
          )?
        ]
      : [
          Omit<
            FetchOptions<FilterKeys<Paths[P], T>> &
              MutationOptions<T extends keyof Paths[P] ? Paths[P][T] : unknown>,
            'body'
          >
        ]
  ): UseMutationResult<
    FetchResponse<T extends keyof Paths[P] ? Paths[P][T] : unknown>['data'],
    FetchResponse<T extends keyof Paths[P] ? Paths[P][T] : unknown>['error'],
    MutationBody<T extends keyof Paths[P] ? Paths[P][T] : unknown>
  >;
};

export const createUseTypedMutation: CreateUseTypedMutation<paths> = {
  // @ts-expect-error It does return the correct type
  useTypedMutation(method, url, ...options) {
    const option = options[0];
    // @ts-expect-error All good, we know this return the correct type
    return useMutation({
      mutationFn: async (body?) => {
        const client = await getClient();
        // @ts-expect-error All good, we know this method exists
        const { data, error } = await client[method.toUpperCase()](url, {
          body: body,
          ...option
        });
        if (error) throw new Error(error);
        return data;
      },
      ...option?.mutationOpts
    });
  }
};

export const useTypedMutation = createUseTypedMutation.useTypedMutation;
